import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../store/reducers';
import { Search, HelpCircle, Bell, Menu, Shield } from 'lucide-react';
import { getRoleNames } from '../utils/roles';
import './TopBar.css';

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  '/espace-cndj': { title: 'Tableau de bord', subtitle: "Vue d'ensemble de l'activité du centre" },
  '/espace-cndj/numerisation': { title: 'Numérisation', subtitle: 'Importer et traiter les décisions' },
  '/espace-cndj/decisions': { title: 'Décisions', subtitle: 'Gérer et publier les décisions' },
  '/espace-cndj/recherche': { title: 'Recherche avancée', subtitle: 'Explorer la base documentaire' },
  '/espace-admin': { title: 'Tableau de bord', subtitle: "Pilotage et supervision de la plateforme" },
  '/espace-admin/utilisateurs': { title: 'Utilisateurs', subtitle: 'Gérer les comptes et les rôles' },
  '/espace-admin/roles': { title: 'Rôles & permissions', subtitle: 'Configurer les rôles et accès' },
  '/espace-admin/reassignation': { title: 'Réassignation', subtitle: 'Réaffecter les décisions entre agents' },
  '/espace-admin/numerisation': { title: 'Numérisation', subtitle: 'Supervision des fichiers numérisés' },
  '/espace-admin/decisions': { title: 'Décisions', subtitle: 'Supervision des décisions' },
  '/espace-admin/historique': { title: 'Historique de connexion', subtitle: 'Suivi des connexions et tentatives' },
  '/espace-cndj/profile': { title: 'Mon profil', subtitle: 'Gérer vos informations personnelles' },
  '/espace-admin/profile': { title: 'Mon profil', subtitle: 'Gérer vos informations personnelles' },
};

interface TopBarProps {
  onMenuClick?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const navigate = useNavigate();

  const basePath = location.pathname.match(/^\/espace-(cndj|admin)(\/[^/]+)?/)?.[0] || '/espace-cndj';
  const isAdmin = location.pathname.startsWith('/espace-admin');
  const pageInfo = pageTitles[basePath] || (isAdmin ? pageTitles['/espace-admin'] : pageTitles['/espace-cndj']);

  const firstName = user?.first_name || '';
  const lastName = user?.last_name || '';
  const displayName = firstName && lastName
    ? `${firstName} ${lastName}`
    : user?.email || 'Utilisateur';

  const initials = firstName && lastName
    ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    : displayName.charAt(0).toUpperCase();

  const roles = getRoleNames(user?.role);
  const roleLabel = roles.includes('admin') || roles.includes('superadmin')
    ? 'Administrateur'
    : roles.includes('validateur')
      ? 'Validateur'
      : roles.includes('agent')
        ? 'Agent CNDJ'
        : 'Utilisateur';

  return (
    <header className="topbar">
      <div className="topbar-left">
        {onMenuClick && (
          <button className="topbar-menu-btn" onClick={onMenuClick}>
            <Menu size={22} />
          </button>
        )}
        <div>
          <div className="topbar-title-row">
            <h1 className="topbar-title">{pageInfo.title}</h1>
            {isAdmin && (
              <span className="topbar-admin-badge">
                <Shield size={12} /> ADMIN
              </span>
            )}
          </div>
          <p className="topbar-subtitle">{pageInfo.subtitle}</p>
        </div>
      </div>

      <div className="topbar-right">
        <div className="topbar-search">
          <Search size={16} className="topbar-search-icon" />
          <input type="text" placeholder={isAdmin ? "Rechercher un utilisateur, un fichier..." : "Rechercher une décision..."} />
        </div>

        <button className="topbar-icon-btn">
          <HelpCircle size={20} />
        </button>

        <button className="topbar-icon-btn topbar-notif-btn">
          <Bell size={20} />
          <span className="topbar-notif-dot" />
        </button>

        <div
          className="topbar-user topbar-user-clickable"
          onClick={() => navigate(isAdmin ? '/espace-admin/profile' : '/espace-cndj/profile')}
        >
          <div className="topbar-avatar">{initials}</div>
          <div className="topbar-user-info">
            <span className="topbar-user-name">{displayName}</span>
            <span className="topbar-user-role">{roleLabel}</span>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
